import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { HintedButton } from "@/components/HintedButton";
import { PageHeader } from "@/components/PageHeader";
import { SectionHeading } from "@/components/SectionHeading";
import type { Status } from "@/components/StatusBadge";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import {
  type SchemaVersion,
  createSchemaVersion,
  submitSchemaVersion,
} from "@/features/schema/api";
import {
  SchemaBuilder,
  definitionFrom,
  draftsFrom,
  useDefinitionError,
} from "@/features/schema/components/SchemaBuilder";
import { activeSchemaQuery, schemaVersionsQuery } from "@/features/schema/queries";

/**
 * Defining a master type's fields.
 *
 * The flow follows what the API allows and stops where it stops: draft a
 * version, read the compatibility classification the server computed for
 * it, then submit for review. There is no activate button because there
 * is no activate route — a version reaches `active` only through the
 * approval flow, and offering a button that skips the reviewer would be
 * offering to break the whole maker-checker model.
 *
 * The classification is the reason draft and submit are two steps rather
 * than one. `compatibility` and `diff` are computed when the draft is
 * stored, so the only honest way to show somebody "this change is
 * breaking" *before* they submit is to save the draft first.
 */
export const Route = createFileRoute("/_authed/master-types/$code/schema")({
  staticData: {
    crumbs: [
      { key: "nav.masterTypes", to: "/master-types" },
      { param: "code" },
      { key: "nav.breadcrumb.schema" },
    ],
  },
  loader: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    await Promise.all([
      context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale)),
      context.queryClient.ensureQueryData(activeSchemaQuery(params.code)),
    ]);
  },
  component: SchemaBuilderPage,
});

function SchemaBuilderPage() {
  const { t } = useTranslation();
  const { code } = Route.useParams();
  const queryClient = useQueryClient();
  const { me, can } = useSession();
  const locale = me.resolved_locale;

  const { data: index } = useSuspenseQuery(masterTypeIndexQuery(locale));
  const { data: active } = useSuspenseQuery(activeSchemaQuery(code));
  const versions = useQuery(schemaVersionsQuery(code));

  const [drafts, setDrafts] = useState(() => draftsFrom(active?.definition));
  const [saved, setSaved] = useState<SchemaVersion | null>(null);

  const error = useDefinitionError(drafts);
  const mayDefine = can("schema.define");
  const maySubmit = can("schema.submit");

  const save = useMutation({
    mutationFn: () => createSchemaVersion(code, definitionFrom(drafts)),
    onSuccess: (version) => {
      setSaved(version);
      void queryClient.invalidateQueries({ queryKey: ["schema", code] });
    },
  });

  const submit = useMutation({
    mutationFn: (schemaId: string) => submitSchemaVersion(schemaId),
    onSuccess: () => {
      toast.success(t("records.detail.submitted"));
      setSaved(null);
      void queryClient.invalidateQueries({ queryKey: ["schema", code] });
    },
  });

  const masterType = index.byCode.get(code);
  const typeName =
    masterType?.display_name[locale] ?? masterType?.display_name[me.tenant.default_locale] ?? code;

  // A reference may point at any type in the registry, including this one
  // — a self-reference is legal (an employee's manager is an employee).
  const targets = index.all.map((type) => ({
    code: type.code,
    label: type.display_name[locale] ?? type.display_name[me.tenant.default_locale] ?? type.code,
  }));

  return (
    <>
      <PageHeader
        title={t("schema.builderTitle", { type: typeName })}
        description={t("schema.description")}
      />

      <p className="mb-4 text-sm text-muted">
        {active === null
          ? t("schema.noActiveVersion")
          : t("schema.activeVersion", { version: active.version })}
      </p>

      <SchemaBuilder
        drafts={drafts}
        onChange={(next) => {
          setDrafts(next);
          // The saved draft no longer describes what is on screen, so the
          // classification below it no longer describes anything.
          setSaved(null);
        }}
        targets={targets}
        disabled={!mayDefine || save.isPending}
        disabledReason={mayDefine ? undefined : t("schema.cannotDefine")}
      />

      {error !== null && (
        <p className="mt-4 rounded-panel border border-danger bg-danger-surface p-3 text-sm text-danger">
          {error}
        </p>
      )}

      {saved !== null && (
        <div className="mt-4 rounded-panel border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold text-foreground">
            {t("schema.compatibility.label")}
          </h2>
          <p className="mt-1 flex items-center gap-2 text-sm">
            <StatusBadge status={saved.compatibility === "breaking" ? "rejected" : "approved"} />
            <span className="text-foreground">
              {saved.compatibility === null
                ? t("schema.compatibility.unknown")
                : t(
                    `schema.compatibility.${saved.compatibility === "breaking" ? "breaking" : "compatible"}`,
                  )}
            </span>
          </p>
          {saved.compatibility === "breaking" && (
            <p className="mt-2 text-sm text-muted">{t("schema.compatibility.breakingBody")}</p>
          )}
          {maySubmit && (
            <Button
              className="mt-3"
              onClick={() => submit.mutate(saved.id)}
              disabled={submit.isPending}
              tooltip={t("tooltips.submitSchema")}
            >
              {t("schema.submit")}
            </Button>
          )}
        </div>
      )}

      {/* Rendered whether or not it will work. PR5 hid it entirely when
          `schema.define` was missing, which left the page with a greyed
          field list, no save control and nothing saying why — the
          combination that reads as a broken page rather than a
          permissions boundary. */}
      <div className="mt-4 flex flex-wrap gap-2">
        <HintedButton
          onClick={() => save.mutate()}
          disabled={mayDefine && (error !== null || drafts.length === 0 || save.isPending)}
          hint={mayDefine ? undefined : t("schema.cannotDefine")}
        >
          {t("schema.saveDraft")}
        </HintedButton>
      </div>

      <div className="mt-8">
        <SectionHeading title={t("schema.versions")} />
      </div>
      <ol className="space-y-2">
        {(versions.data?.items ?? [])
          .slice()
          .reverse()
          .map((version) => (
            <li
              key={version.id}
              className="flex flex-wrap items-center gap-2 rounded-panel border border-border bg-surface p-3 text-sm shadow-panel"
            >
              <span className="font-medium text-foreground">
                {t("records.detail.version", { version: version.version })}
              </span>
              {/* `StatusBadge`, not the raw enum. This was printing
                  "approved"/"pending" straight from the API — untranslated,
                  so a Hindi session got an English word, and styled
                  differently from the same status everywhere else in the
                  app. */}
              <StatusBadge status={statusOf(version.status)} />
              {version.compatibility !== null && (
                <span className="font-caption text-muted text-xs">{version.compatibility}</span>
              )}
            </li>
          ))}
      </ol>
    </>
  );
}

/** A schema version's own lifecycle. Narrowed to the badge's vocabulary
 *  rather than passed through, so an unexpected value from the API renders
 *  as a draft rather than crashing the list. */
function statusOf(status: string): Status {
  if (status === "approved") return "approved";
  if (status === "pending") return "pending";
  if (status === "rejected") return "rejected";
  return "draft";
}
